import { Link } from 'react-router';
import { SectionCard } from '@/components/ui';
import type { TopSellingProduct } from '../types/dashboardWidget';

interface TopSellingListProps {
  items?: TopSellingProduct[] | undefined;
  isLoading?: boolean | undefined;
  title: string;
  labels: {
    rank: string;
    product: string;
    sales: string;
    revenue: string;
    viewAll: string;
    empty: string;
  };
}

const formatKRW = (value: number) =>
  new Intl.NumberFormat('ko-KR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

const RANK_COLORS = ['var(--accent-default)', 'var(--signal-warn)', 'var(--signal-info)'];

export const TopSellingList = ({
  items,
  isLoading,
  title,
  labels,
}: TopSellingListProps) => (
  <SectionCard
    title={title}
    aside={
      <Link
        to="/products"
        className="rounded-full px-3 py-1 text-xs font-medium transition-all hover:bg-[color:var(--surface-1)]"
        style={{ color: 'var(--content-muted)', border: '1px solid var(--divider-soft)' }}
      >
        {labels.viewAll} →
      </Link>
    }
  >
    {isLoading ? (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-8 animate-pulse rounded"
            style={{ background: 'var(--surface-1)' }}
          />
        ))}
      </div>
    ) : (items ?? []).length === 0 ? (
      <p
        className="py-8 text-center text-sm"
        style={{ color: 'var(--content-muted)' }}
      >
        {labels.empty}
      </p>
    ) : (
      <ol className="space-y-2">
        {(items ?? []).slice(0, 5).map((item, idx) => {
          const rank = idx + 1;
          const rankColor =
            rank <= 3 ? RANK_COLORS[rank - 1] : 'var(--content-muted)';
          return (
            <li
              key={item.productId ?? idx}
              className="flex items-center gap-3 rounded-lg px-3 py-2"
              style={{ background: 'var(--surface-1)' }}
            >
              <span
                className="w-5 shrink-0 text-center text-sm font-bold tabular-nums"
                style={{ color: rankColor }}
              >
                {rank}
              </span>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/products/${item.productId ?? ''}`}
                  className="block truncate text-sm font-medium hover:underline"
                  style={{ color: 'var(--content-strong)' }}
                >
                  {item.productName ?? '—'}
                </Link>
                <span
                  className="text-xs tabular-nums"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {new Intl.NumberFormat('ko-KR').format(item.salesCount ?? 0)}건
                </span>
              </div>
              <span
                className="shrink-0 text-sm font-semibold tabular-nums"
                style={{ color: 'var(--content-strong)' }}
              >
                {formatKRW(item.revenue ?? 0)}원
              </span>
            </li>
          );
        })}
      </ol>
    )}
  </SectionCard>
);
